import { sanitizeRichTextHtml, sanitizeStylesheet } from './utils/sanitizeHtml';
import React, { useEffect, useId } from 'react';
import { Button, Tooltip } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  UndoOutlined,
  RedoOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined
} from '@ant-design/icons';

// Tiptap imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import Image from '@tiptap/extension-image';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlock from '@tiptap/extension-code-block';
import Code from '@tiptap/extension-code';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import HardBreak from '@tiptap/extension-hard-break';
import { Node, Mark } from '@tiptap/core';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';

interface ChapterTextEditorProps {
  initialContent: string; // full HTML/XML for the chapter content (we'll extract & edit only <body> inner HTML)
  onChange: (editedBodyHtml: string) => void; // emits the editor's body HTML on updates and after initial set
  height?: string; // outer container height (defaults to previous layout)
  styleContent?: string; // optional CSS extracted from <style> tags to keep class-based formatting visible
}

// Helpers to preserve data-* attributes
const collectDataset = (el: Element) => {
  const out: Record<string, string> = {};
  for (const name of el.getAttributeNames()) {
    if (name.startsWith('data-')) {
      out[name.slice(5)] = el.getAttribute(name) || '';
    }
  }
  return Object.keys(out).length ? out : null;
};

const spreadDataset = (dataAttrs?: Record<string, string> | null) => {
  if (!dataAttrs) return {};
  const pairs: Record<string, string> = {};
  for (const [k, v] of Object.entries(dataAttrs)) {
    pairs[`data-${k}`] = v;
  }
  return pairs;
};

// Paragraph with attribute preservation
const ParagraphWithAttrs = Paragraph.extend({
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['p', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Heading with attribute preservation
const HeadingWithAttrs = Heading.extend({
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const level = (this.options.levels as number[]).includes(node.attrs.level)
      ? node.attrs.level
      : (this.options.levels as number[])[0];
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return [`h${level}`, { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Div block node to preserve wrappers
const DivNode = Node.create({
  name: 'div',
  group: 'block',
  content: 'block*',
  parseHTML() {
    return [{ tag: 'div' }];
  },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['div', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Span mark to preserve inline formatting spans (e.g., dropcap, lead_word)
const SpanMark = Mark.create({
  name: 'span',
  inclusive: true,
  parseHTML() {
    return [{ tag: 'span' }];
  },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ mark }: { mark: any }) {
    const { dataAttrs, ...rest } = ((mark?.attrs ?? {}) as any);
    return ['span', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Image with attribute preservation
const ImageWithAttrs = Image.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      title: { default: null },
      alt: { default: null },
      'epub:type': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['img', { ...rest, ...spreadDataset(dataAttrs) }];
  }
});

// Link with attribute preservation
const LinkWithAttrs = Link.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = (HTMLAttributes ?? {}) as any;
    return ['a', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Blockquote with attribute preservation
const BlockquoteWithAttrs = Blockquote.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['blockquote', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Code block (<pre>) with attribute preservation
const CodeBlockWithAttrs = CodeBlock.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      'epub:type': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node, HTMLAttributes }: { node: any, HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['pre', { ...rest, ...spreadDataset(dataAttrs) }, ['code', 0]];
  }
});

// Inline <code> mark with attribute preservation
const CodeMarkWithAttrs = Code.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      'epub:type': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ mark, HTMLAttributes }: { mark: any, HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['code', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Horizontal rule <hr> with attribute preservation
const HorizontalRuleWithAttrs = HorizontalRule.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      'epub:type': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['hr', { ...rest, ...spreadDataset(dataAttrs) }];
  }
});

// Hard break <br> with attribute preservation
const HardBreakWithAttrs = HardBreak.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      'epub:type': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['br', { ...rest, ...spreadDataset(dataAttrs) }];
  }
});

// Bullet list with attribute preservation
const BulletListWithAttrs = BulletList.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['ul', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Ordered list with attribute preservation (start, reversed, type)
const OrderedListWithAttrs = OrderedList.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      start: { default: 1 },
      reversed: { default: null },
      type: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['ol', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// List item with attribute preservation (value)
const ListItemWithAttrs = ListItem.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      value: { default: null },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['li', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Minimal table support with attribute preservation
const TableNode = Node.create({
  name: 'table',
  group: 'block',
  content: '(tableHead|tableFoot|tableColGroup|tableRow)+',
  parseHTML() {
    return [{ tag: 'table' }];
  },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['table', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const TableRowNode = Node.create({
  name: 'tableRow',
  group: 'block',
  content: '(tableCell|tableHeader)+',
  parseHTML() {
    return [{ tag: 'tr' }];
  },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['tr', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const TableCellNode = Node.create({
  name: 'tableCell',
  group: 'block',
  content: 'block+',
  isolating: true,
  parseHTML() {
    return [{ tag: 'td' }];
  },
  addAttributes() {
    return {
      colspan: { default: 1 },
      rowspan: { default: 1 },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['td', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const TableHeaderNode = Node.create({
  name: 'tableHeader',
  group: 'block',
  content: 'block+',
  isolating: true,
  parseHTML() {
    return [{ tag: 'th' }];
  },
  addAttributes() {
    return {
      colspan: { default: 1 },
      rowspan: { default: 1 },
      scope: { default: null },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['th', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Table head section <thead>
const TableHeadNode = Node.create({
  name: 'tableHead',
  group: 'block',
  content: 'tableRow+',
  parseHTML() {
    return [{ tag: 'thead' }];
  },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['thead', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Table foot section <tfoot>
const TableFootNode = Node.create({
  name: 'tableFoot',
  group: 'block',
  content: 'tableRow+',
  parseHTML() {
    return [{ tag: 'tfoot' }];
  },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['tfoot', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Table column group <colgroup>
const TableColGroupNode = Node.create({
  name: 'tableColGroup',
  group: 'block',
  content: 'tableCol+',
  parseHTML() {
    return [{ tag: 'colgroup' }];
  },
  addAttributes() {
    return {
      span: { default: null },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['colgroup', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

// Table column <col>
const TableColNode = Node.create({
  name: 'tableCol',
  group: 'block',
  atom: true,
  parseHTML() {
    return [{ tag: 'col' }];
  },
  addAttributes() {
    return {
      span: { default: null },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return ['col', { ...rest, ...spreadDataset(dataAttrs) }];
  }
});

// Semantic block containers
const makeBlockContainer = (tag: string, name?: string) => Node.create({
  name: name || tag,
  group: 'block',
  content: 'block*',
  parseHTML() { return [{ tag }]; },
  addAttributes() {
    return {
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ node }: { node: any }) {
    const { dataAttrs, ...rest } = ((node?.attrs ?? {}) as any);
    return [tag, { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const SectionNode = makeBlockContainer('section');
const ArticleNode = makeBlockContainer('article');
const AsideNode = makeBlockContainer('aside');
const HeaderNode = makeBlockContainer('header');
const FooterNode = makeBlockContainer('footer');
const NavNode = makeBlockContainer('nav');
const MainNode = makeBlockContainer('main');
const FigureNode = makeBlockContainer('figure');
const FigcaptionNode = makeBlockContainer('figcaption');

// Tiptap table extensions with attribute preservation
const TableWithAttrs = Table.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      role: { default: null },
      lang: { default: null },
      'xml:lang': { default: null },
      'epub:type': { default: null },
      'aria-hidden': { default: null },
      'aria-label': { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['table', { ...rest, ...spreadDataset(dataAttrs) }, ['tbody', 0]];
  }
});

const TableRowWithAttrs = TableRow.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['tr', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const TableHeaderWithAttrs = TableHeader.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      colspan: { default: 1 },
      rowspan: { default: 1 },
      scope: { default: null },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['th', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const TableCellWithAttrs = TableCell.extend({
  addAttributes() {
    const base = (this.parent?.() as Record<string, any>) ?? {};
    return {
      ...base,
      colspan: { default: 1 },
      rowspan: { default: 1 },
      id: { default: null },
      class: { default: null },
      style: { default: null },
      dataAttrs: {
        default: null,
        parseHTML: (element: Element) => collectDataset(element),
        renderHTML: () => ({})
      }
    };
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    const { dataAttrs, ...rest } = ((HTMLAttributes ?? {}) as any);
    return ['td', { ...rest, ...spreadDataset(dataAttrs) }, 0];
  }
});

const NeverTranslate = Mark.create({
  name: 'neverTranslate',
  inclusive: false,
  parseHTML: () => [{ tag: 'nt' }],
  renderHTML: () => ['nt', {}, 0],
});

// Toolbar component for Tiptap
const MenuBar = ({ editor }: { editor: any }) => {
  if (!editor) return null;
  // Table toolbar removed per request; table editing still supported via shortcuts/context.
  return (
    <div className="chapter-editor-toolbar" data-testid="tutorial:chapter:editor-toolbar">
      <Tooltip title="Bold">
        <Button
          aria-label="Bold"
          size="small"
          type={editor.isActive('bold') ? 'primary' : 'default'}
          icon={<BoldOutlined />}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
      </Tooltip>
      <Tooltip title="Italic">
        <Button
          aria-label="Italic"
          size="small"
          type={editor.isActive('italic') ? 'primary' : 'default'}
          icon={<ItalicOutlined />}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
      </Tooltip>
      <Tooltip title="Underline">
        <Button
          aria-label="Underline"
          size="small"
          type={editor.isActive('underline') ? 'primary' : 'default'}
          icon={<UnderlineOutlined />}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
      </Tooltip>
      <Tooltip title="Strikethrough">
        <Button
          aria-label="Strikethrough"
          size="small"
          type={editor.isActive('strike') ? 'primary' : 'default'}
          icon={<StrikethroughOutlined />}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
      </Tooltip>
      <div className="chapter-editor-divider" />
      <Tooltip title="Bulleted list">
        <Button
          aria-label="Bulleted list"
          size="small"
          type={editor.isActive('bulletList') ? 'primary' : 'default'}
          icon={<UnorderedListOutlined />}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
      </Tooltip>
      <Tooltip title="Numbered list">
        <Button
          aria-label="Numbered list"
          size="small"
          type={editor.isActive('orderedList') ? 'primary' : 'default'}
          icon={<OrderedListOutlined />}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
      </Tooltip>
      <div className="chapter-editor-divider" />
      <Tooltip title="Align left">
        <Button
          aria-label="Align left"
          size="small"
          type={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
          icon={<AlignLeftOutlined />}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        />
      </Tooltip>
      <Tooltip title="Align center">
        <Button
          aria-label="Align center"
          size="small"
          type={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
          icon={<AlignCenterOutlined />}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        />
      </Tooltip>
      <Tooltip title="Align right">
        <Button
          aria-label="Align right"
          size="small"
          type={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
          icon={<AlignRightOutlined />}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        />
      </Tooltip>
      <div className="chapter-editor-divider" />
      <Tooltip title="Undo">
        <Button
          aria-label="Undo"
          size="small"
          icon={<UndoOutlined />}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        />
      </Tooltip>
      <Tooltip title="Redo">
        <Button
          aria-label="Redo"
          size="small"
          icon={<RedoOutlined />}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        />
      </Tooltip>
      {/* Table tools removed for now */}
    </div>
  );
};

// Extract inner <body> HTML; if no <body> exists, return provided content
const extractBodyContent = (htmlContent: string): string => {
  if (!htmlContent) return '';
  const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const inner = bodyMatch && bodyMatch[1] ? bodyMatch[1] : htmlContent;
  const withoutStyles = inner.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  return withoutStyles.trim();
};

const ChapterTextEditor: React.FC<ChapterTextEditorProps> = ({
  initialContent,
  onChange,
  height = '100%',
  styleContent
}) => {
  const manuscriptScope = useId().replace(/[^a-zA-Z0-9_-]/g, '_');
  // Tiptap editor configuration
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          paragraph: false,
          heading: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          horizontalRule: false,
          hardBreak: false,
          bulletList: false,
          orderedList: false,
          listItem: false
        }),
        // Attribute-preserving nodes
        ParagraphWithAttrs,
        HeadingWithAttrs,
        DivNode,
        SpanMark,
        ImageWithAttrs.configure({ allowBase64: true }),
        BlockquoteWithAttrs,
        CodeBlockWithAttrs,
        CodeMarkWithAttrs,
        HorizontalRuleWithAttrs,
        HardBreakWithAttrs,
        BulletListWithAttrs,
        OrderedListWithAttrs,
        ListItemWithAttrs,
        TableWithAttrs.configure({ resizable: false }),
        TableRowWithAttrs,
        TableHeaderWithAttrs,
        TableCellWithAttrs,
        SectionNode,
        ArticleNode,
        AsideNode,
        HeaderNode,
        FooterNode,
        NavNode,
        MainNode,
        FigureNode,
        FigcaptionNode,
        Underline,
        NeverTranslate,
        LinkWithAttrs.configure({
          openOnClick: false
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph']
        })
      ],
      content: '',
      editorProps: {
        attributes: {
          style: 'padding: 10px;',
          'data-testid': 'tutorial:chapter:editor-body',
          'aria-label': 'Chapter editor body'
        },
        transformPastedHTML(html: string) {
          return sanitizeRichTextHtml(html);
        }
      },
      parseOptions: {
        preserveWhitespace: 'full'
      },
      onUpdate: ({ editor }) => {
        onChange(sanitizeRichTextHtml(editor.getHTML()));
      }
    },
    []
  );

  // Initialize or update editor content when initialContent changes
  useEffect(() => {
    if (!editor) return;
    const bodyContent = sanitizeRichTextHtml(extractBodyContent(initialContent));
    editor.commands.setContent(bodyContent);
  }, [editor, initialContent]);

  return (
    <div className="chapter-editor-shell" style={{ height }} data-testid="tutorial:chapter:editor-shell">
      {styleContent ? (
        <style>{sanitizeStylesheet(styleContent, manuscriptScope)}</style>
      ) : null}
      <MenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="chapter-editor-content"
        data-manuscript-scope={manuscriptScope}
      />
    </div>
  );
};

export default ChapterTextEditor;
